/**
 * ProfileAddSheet (web): Gemini-styled alert dialog.
 *
 * Centered modal that presents quick-add tiles, a suggested block list and an
 * explore grid that opens into block categories. All controls follow the
 * Tabbio web design system: white pill search, neutral icon discs, sentence
 * case labels, single-line block rows with a chevron.
 */
import React, { useCallback, useMemo, useState } from 'react';
import { useRouter } from 'expo-router';
import { useTranslation } from 'react-i18next';
import { WebDialog } from '../common/WebDialog.web';
import { MaterialSymbol } from '../icons/MaterialSymbol';
import { usePublicProfileBuilderModel } from './PublicProfileBuilderProvider';
import {
  PUBLIC_PROFILE_ADD_BASICS_COMPANY,
  PUBLIC_PROFILE_BLOCK_META,
  PUBLIC_PROFILE_EXPLORE_GROUP_BY_TYPE,
  PUBLIC_PROFILE_EXPLORE_GROUP_ORDER,
  PUBLIC_PROFILE_QUICK_ADD_ITEMS,
  createNewBlock,
  isPublicProfileBlockSupported,
  type PublicProfileBlockType,
  type PublicProfileExploreGroupId,
  type PublicProfileQuickAddItem,
} from '../../lib/public-profile-builder';
import { getPublicProfileRoutes } from '../../lib/public-profile-routes';
import { proposeBlockFromUrl, resolveCombinedAddInput } from '../../lib/smart-add';

const MATERIAL_ICON_BY_TYPE: Record<PublicProfileBlockType, string> = {
  webcv: 'description',
  career: 'work',
  services: 'storefront',
  website: 'language',
  insights: 'insights',
  faqs: 'help',
  links: 'link',
  visit: 'place',
  openroles: 'work',
  team: 'groups',
  talentpool: 'groups_3',
  portfolio: 'grid_view',
  testimonial: 'reviews',
  booking: 'event',
  map: 'map',
  pdf: 'picture_as_pdf',
  image: 'image',
  video: 'play_circle',
  digitalproduct: 'inventory_2',
  text: 'subject',
  header: 'title',
  form: 'description',
  music: 'music_note',
};

const QUICK_ICON_BY_KEY: Record<PublicProfileQuickAddItem['iconKey'], string> = {
  collection: 'grid_view',
  link: 'link',
  header: 'title',
  form: 'description',
};

// Company Add sheet glyphs: the website offering is labelled "Link" (PRD
// §6.4), so it carries the link glyph rather than the generic website one.
const COMPANY_ADD_ICON_BY_TYPE: Partial<Record<PublicProfileBlockType, string>> = {
  website: 'link',
  text: 'subject',
};

export function ProfileAddSheet() {
  const router = useRouter();
  const { t } = useTranslation('common');
  const tx = useCallback((key: string) => String(t(key as never)), [t]);

  const {
    blocks,
    routeMode,
    suggestedBlocks,
    exploreTiles,
    addBlock,
    setBlocks,
  } = usePublicProfileBuilderModel();

  const profileRoutes = useMemo(
    () => getPublicProfileRoutes(routeMode),
    [routeMode],
  );

  const suggestedItems = useMemo(
    () => (Array.isArray(suggestedBlocks) ? suggestedBlocks : []),
    [suggestedBlocks],
  );
  const exploreItems = useMemo(
    () => (Array.isArray(exploreTiles) ? exploreTiles : []),
    [exploreTiles],
  );
  const quickAddItems = useMemo(
    () =>
      PUBLIC_PROFILE_QUICK_ADD_ITEMS.filter((item) =>
        isPublicProfileBlockSupported(item.type, routeMode),
      ),
    [routeMode],
  );

  const [search, setSearch] = useState('');
  const [selectedExplore, setSelectedExplore] = useState<string | null>(null);
  const [pasteUrl, setPasteUrl] = useState('');

  // Smart add (PRD §6.5): a pasted URL proposes a block; the employer reviews
  // the proposal and confirms with an explicit Add/Update action.
  const smartAddProposal = useMemo(
    () => proposeBlockFromUrl(pasteUrl, { existingBlocks: blocks, mode: routeMode }),
    [blocks, pasteUrl, routeMode],
  );
  const showSmartAddInvalid = Boolean(pasteUrl.trim()) && !smartAddProposal;

  const handleSmartAdd = useCallback(() => {
    if (!smartAddProposal) return;
    setPasteUrl('');

    if (smartAddProposal.kind === 'update-unique' && smartAddProposal.existingBlockId) {
      const targetId = smartAddProposal.existingBlockId;
      setBlocks((previous) =>
        previous.map((block) =>
          block.id === targetId
            ? { ...block, config: { ...block.config, url: smartAddProposal.config.url } }
            : block,
        ),
      );
      router.replace(profileRoutes.block(targetId) as never);
      return;
    }

    const base = createNewBlock(smartAddProposal.type);
    const created = {
      ...base,
      // A Links block keeps its destinations in `items`; every other type has a
      // single `config.url`.
      config:
        smartAddProposal.type === 'links'
          ? {
              ...base.config,
              items: [
                {
                  id: `item_${Date.now()}`,
                  title: '',
                  url: smartAddProposal.config.url,
                },
              ],
            }
          : { ...base.config, url: smartAddProposal.config.url },
    };
    setBlocks((previous) => [
      created,
      ...previous.map((block, index) => ({ ...block, order: index + 1 })),
    ]);
    router.replace(profileRoutes.block(created.id) as never);
  }, [profileRoutes, router, setBlocks, smartAddProposal]);

  const isCompany = routeMode === 'company';

  // Company mode has ONE combined input: a pasted URL shows the smart-add
  // proposal, a valid-but-unsupported URL shows a hint, anything else filters
  // the offered basics.
  const combinedInput = useMemo(
    () => resolveCombinedAddInput(pasteUrl, { existingBlocks: blocks, mode: routeMode }),
    [blocks, pasteUrl, routeMode],
  );

  const companyBlockTitle = useCallback(
    (type: PublicProfileBlockType) =>
      type === 'website'
        ? tx('publicProfile.addSheet.company.linkLabel')
        : tx(PUBLIC_PROFILE_BLOCK_META[type].titleKey),
    [tx],
  );

  const companyBasicsTypes = useMemo(() => {
    if (combinedInput.kind !== 'search') {
      return PUBLIC_PROFILE_ADD_BASICS_COMPANY;
    }
    const q = combinedInput.query.toLowerCase();
    return PUBLIC_PROFILE_ADD_BASICS_COMPANY.filter((type) => {
      const meta = PUBLIC_PROFILE_BLOCK_META[type];
      return [companyBlockTitle(type), tx(meta.titleKey), tx(meta.descKey)]
        .join(' ')
        .toLowerCase()
        .includes(q);
    });
  }, [combinedInput, companyBlockTitle, tx]);

  // Link and Text are not unique blocks (PRD §6.5 lists the unique set), so a
  // company add always creates a fresh block even when one already exists.
  const handleCompanyAdd = useCallback(
    (type: PublicProfileBlockType) => {
      const created = createNewBlock(type);
      setBlocks((previous) => [
        created,
        ...previous.map((block, index) => ({ ...block, order: index + 1 })),
      ]);
      router.replace(profileRoutes.block(created.id) as never);
    },
    [profileRoutes, router, setBlocks],
  );

  const allTypes = useMemo(() => {
    const set = new Set<PublicProfileBlockType>();
    suggestedItems.forEach((item) => set.add(item.type));
    exploreItems.forEach((tile) =>
      tile.types.forEach((type) => set.add(type)),
    );
    return Array.from(set.values()).filter((type) =>
      isPublicProfileBlockSupported(type, routeMode),
    );
  }, [exploreItems, routeMode, suggestedItems]);

  const visibleTypes = useMemo(() => {
    if (search.trim()) {
      const q = search.toLowerCase().trim();
      return allTypes.filter((type) => {
        const meta = PUBLIC_PROFILE_BLOCK_META[type];
        const title = tx(meta.titleKey).toLowerCase();
        const desc = tx(meta.descKey).toLowerCase();
        return title.includes(q) || desc.includes(q);
      });
    }
    if (selectedExplore) {
      const tile = exploreItems.find((item) => item.id === selectedExplore);
      return tile?.types ?? [];
    }
    return [];
  }, [allTypes, exploreItems, search, selectedExplore, tx]);

  const groupedVisibleTypes = useMemo(() => {
    const groups = new Map<PublicProfileExploreGroupId, PublicProfileBlockType[]>();
    visibleTypes.forEach((type) => {
      const groupId =
        PUBLIC_PROFILE_EXPLORE_GROUP_BY_TYPE[type] ?? 'other';
      const previous = groups.get(groupId) ?? [];
      groups.set(groupId, [...previous, type]);
    });
    return PUBLIC_PROFILE_EXPLORE_GROUP_ORDER.filter(
      (groupId) => (groups.get(groupId) ?? []).length > 0,
    ).map((groupId) => ({
      id: groupId,
      types: groups.get(groupId) ?? [],
    }));
  }, [visibleTypes]);

  const closeDialog = useCallback(() => {
    if (typeof window !== 'undefined' && window.history.length > 1) {
      router.back();
      return;
    }
    router.replace(profileRoutes.index as never);
  }, [profileRoutes.index, router]);

  const goBack = useCallback(() => {
    if (search) {
      setSearch('');
      return;
    }
    if (selectedExplore) {
      setSelectedExplore(null);
      return;
    }
    closeDialog();
  }, [closeDialog, search, selectedExplore]);

  const handleAdd = useCallback(
    (type: PublicProfileBlockType) => {
      const existing = blocks.find((block) => block.type === type);
      if (existing) {
        router.replace(
          (type === 'openroles'
            ? profileRoutes.openRoles
            : type === 'team'
              ? profileRoutes.team
              : profileRoutes.block(existing.id)) as never,
        );
        return;
      }

      const created = addBlock(type);
      const path =
        type === 'openroles'
          ? profileRoutes.openRoles
          : type === 'team'
            ? profileRoutes.team
            : `${profileRoutes.block(created.id)}${
                type === 'portfolio' || type === 'services'
                  ? '?intent=create-source'
                  : ''
              }`;
      router.replace(path as never);
    },
    [addBlock, blocks, profileRoutes, router],
  );

  const showBrowse = Boolean(search.trim() || selectedExplore);

  // Company mode (Employer Phase 1): title + one combined input + a two-card
  // "Basics" grid. No suggested list, no explore tiles, no browse groups.
  if (isCompany) {
    return (
      <WebDialog
        title={tx('publicProfile.addSheet.company.title')}
        subtitle={tx('publicProfile.addSheet.company.subtitle')}
        onClose={closeDialog}
        maxWidth={620}
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            <SearchPill
              value={pasteUrl}
              onChange={setPasteUrl}
              placeholder={tx('publicProfile.addSheet.company.combinedPlaceholder')}
            />
            {combinedInput.kind === 'unsupported-url' ? (
              <div
                style={{
                  fontSize: 12.5,
                  lineHeight: '17px',
                  color: 'var(--text-tertiary)',
                  padding: '0 6px',
                  fontFamily:
                    "'Google Sans Text', 'Google Sans', Roboto, Arial, sans-serif",
                }}
              >
                {tx('publicProfile.addSheet.smartAdd.invalid')}
              </div>
            ) : null}
            {smartAddProposal ? (
              <SmartAddProposalRow
                iconName={
                  COMPANY_ADD_ICON_BY_TYPE[smartAddProposal.type] ??
                  MATERIAL_ICON_BY_TYPE[smartAddProposal.type] ??
                  'apps'
                }
                title={companyBlockTitle(smartAddProposal.type)}
                url={smartAddProposal.config.url}
                confirmLabel={
                  smartAddProposal.kind === 'update-unique'
                    ? tx('publicProfile.addSheet.smartAdd.update')
                    : tx('actions.add')
                }
                onConfirm={handleSmartAdd}
              />
            ) : null}
          </div>

          <Section title={tx('publicProfile.addSheet.company.basicsGroup')}>
            {companyBasicsTypes.length === 0 ? (
              <EmptyState
                label={tx('publicProfile.addSheet.company.noMatches')}
                fallback="No blocks match."
              />
            ) : (
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(2, minmax(0,1fr))',
                  gap: 10,
                }}
              >
                {companyBasicsTypes.map((type) => (
                  <QuickAddTile
                    key={type}
                    iconName={
                      COMPANY_ADD_ICON_BY_TYPE[type] ??
                      MATERIAL_ICON_BY_TYPE[type] ??
                      'apps'
                    }
                    label={companyBlockTitle(type)}
                    onPress={() => handleCompanyAdd(type)}
                  />
                ))}
              </div>
            )}
          </Section>
        </div>
      </WebDialog>
    );
  }

  return (
    <WebDialog
      title={tx('publicProfile.addSheet.title')}
      subtitle={
        search
          ? undefined
          : selectedExplore
            ? tx(
                `publicProfile.explore.${
                  exploreItems.find((item) => item.id === selectedExplore)
                    ?.labelKey?.replace('publicProfile.explore.', '') ?? ''
                }`,
              )
            : tx('publicProfile.addSheet.subtitle')
      }
      onClose={closeDialog}
      onBack={showBrowse ? goBack : undefined}
      maxWidth={620}
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
        {/* Smart add: paste a URL, review the proposed block */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          <SearchPill
            value={pasteUrl}
            onChange={setPasteUrl}
            placeholder={tx('publicProfile.addSheet.smartAdd.placeholder')}
            iconName="link"
          />
          {showSmartAddInvalid ? (
            <div
              style={{
                fontSize: 12.5,
                lineHeight: '17px',
                color: 'var(--text-tertiary)',
                padding: '0 6px',
                fontFamily:
                  "'Google Sans Text', 'Google Sans', Roboto, Arial, sans-serif",
              }}
            >
              {tx('publicProfile.addSheet.smartAdd.invalid')}
            </div>
          ) : null}
          {smartAddProposal ? (
            <BlockList>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 12,
                  padding: '12px 16px',
                  boxSizing: 'border-box',
                }}
              >
                <div
                  style={{
                    flexShrink: 0,
                    width: 36,
                    height: 36,
                    borderRadius: 18,
                    background: 'rgba(0,0,0,0.06)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: 'var(--text-primary)',
                  }}
                >
                  <MaterialSymbol
                    name={MATERIAL_ICON_BY_TYPE[smartAddProposal.type] ?? 'apps'}
                    size={18}
                  />
                </div>
                <div
                  style={{
                    flex: 1,
                    minWidth: 0,
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 2,
                  }}
                >
                  <div
                    style={{
                      fontSize: 15,
                      fontWeight: 500,
                      color: 'var(--text-primary)',
                      lineHeight: '20px',
                      fontFamily:
                        "'Google Sans Text', 'Google Sans', Roboto, Arial, sans-serif",
                    }}
                  >
                    {tx(PUBLIC_PROFILE_BLOCK_META[smartAddProposal.type].titleKey)}
                  </div>
                  <div
                    style={{
                      fontSize: 13,
                      lineHeight: '18px',
                      color: 'var(--text-tertiary)',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      fontFamily:
                        "'Google Sans Text', 'Google Sans', Roboto, Arial, sans-serif",
                    }}
                  >
                    {smartAddProposal.config.url}
                  </div>
                </div>
                <button
                  type="button"
                  onClick={handleSmartAdd}
                  style={{
                    all: 'unset',
                    cursor: 'pointer',
                    height: 34,
                    padding: '0 14px',
                    borderRadius: 9999,
                    background: 'var(--text-primary)',
                    color: 'var(--bg)',
                    fontSize: 13,
                    fontWeight: 600,
                    display: 'inline-flex',
                    alignItems: 'center',
                    flexShrink: 0,
                    fontFamily:
                      "'Google Sans Text', 'Google Sans', Roboto, Arial, sans-serif",
                  }}
                >
                  {smartAddProposal.kind === 'update-unique'
                    ? tx('publicProfile.addSheet.smartAdd.update')
                    : tx('actions.add')}
                </button>
              </div>
            </BlockList>
          ) : null}
        </div>

        <SearchPill
          value={search}
          onChange={setSearch}
          placeholder={tx('publicProfile.addSheet.searchPlaceholder')}
        />

        {!showBrowse ? (
          <>
            {quickAddItems.length > 0 && (
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(2, minmax(0,1fr))',
                  gap: 10,
                }}
              >
                {quickAddItems.map((item) => (
                  <QuickAddTile
                    key={item.type}
                    iconName={QUICK_ICON_BY_KEY[item.iconKey]}
                    label={tx(item.labelKey)}
                    onPress={() => handleAdd(item.type)}
                  />
                ))}
              </div>
            )}

            <Section title={tx('publicProfile.addSheet.suggested')}>
              <BlockList>
                {suggestedItems.map((item, index) => (
                  <BlockRow
                    key={`${item.type}-${index}`}
                    iconName={MATERIAL_ICON_BY_TYPE[item.type] ?? 'apps'}
                    title={tx(PUBLIC_PROFILE_BLOCK_META[item.type].titleKey)}
                    desc={tx(PUBLIC_PROFILE_BLOCK_META[item.type].descKey)}
                    onPress={() => handleAdd(item.type)}
                    isLast={index === suggestedItems.length - 1}
                  />
                ))}
              </BlockList>
            </Section>

            {exploreItems.length > 0 && (
              <Section title={tx('publicProfile.addSheet.explore')}>
                <div
                  style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(2, minmax(0,1fr))',
                    gap: 10,
                  }}
                >
                  {exploreItems.map((tile) => (
                    <ExploreTile
                      key={tile.id}
                      iconName={
                        MATERIAL_ICON_BY_TYPE[tile.types[0]] ?? 'category'
                      }
                      label={tx(tile.labelKey)}
                      count={tile.types.length}
                      onPress={() => setSelectedExplore(tile.id)}
                    />
                  ))}
                </div>
              </Section>
            )}
          </>
        ) : (
          <>
            {groupedVisibleTypes.length === 0 ? (
              <EmptyState
                label={tx('publicProfile.addSheet.searchEmpty')}
                fallback="No blocks match."
              />
            ) : (
              groupedVisibleTypes.map((group) => (
                <Section
                  key={group.id}
                  title={tx(`publicProfile.addSheet.groups.${group.id}`)}
                >
                  <BlockList>
                    {group.types.map((type, index) => (
                      <BlockRow
                        key={type}
                        iconName={MATERIAL_ICON_BY_TYPE[type] ?? 'apps'}
                        title={tx(PUBLIC_PROFILE_BLOCK_META[type].titleKey)}
                        desc={tx(PUBLIC_PROFILE_BLOCK_META[type].descKey)}
                        onPress={() => handleAdd(type)}
                        isLast={index === group.types.length - 1}
                      />
                    ))}
                  </BlockList>
                </Section>
              ))
            )}
          </>
        )}
      </div>
    </WebDialog>
  );
}

function SmartAddProposalRow({
  iconName,
  title,
  url,
  confirmLabel,
  onConfirm,
}: {
  iconName: string;
  title: string;
  url: string;
  confirmLabel: string;
  onConfirm: () => void;
}) {
  return (
    <BlockList>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 12,
          padding: '12px 16px',
          boxSizing: 'border-box',
        }}
      >
        <div
          style={{
            flexShrink: 0,
            width: 36,
            height: 36,
            borderRadius: 18,
            background: 'rgba(0,0,0,0.06)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: 'var(--text-primary)',
          }}
        >
          <MaterialSymbol name={iconName} size={18} />
        </div>
        <div
          style={{
            flex: 1,
            minWidth: 0,
            display: 'flex',
            flexDirection: 'column',
            gap: 2,
          }}
        >
          <div
            style={{
              fontSize: 15,
              fontWeight: 500,
              color: 'var(--text-primary)',
              lineHeight: '20px',
              fontFamily:
                "'Google Sans Text', 'Google Sans', Roboto, Arial, sans-serif",
            }}
          >
            {title}
          </div>
          <div
            style={{
              fontSize: 13,
              lineHeight: '18px',
              color: 'var(--text-tertiary)',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              fontFamily:
                "'Google Sans Text', 'Google Sans', Roboto, Arial, sans-serif",
            }}
          >
            {url}
          </div>
        </div>
        <button
          type="button"
          onClick={onConfirm}
          style={{
            all: 'unset',
            cursor: 'pointer',
            height: 34,
            padding: '0 14px',
            borderRadius: 9999,
            background: 'var(--text-primary)',
            color: 'var(--bg)',
            fontSize: 13,
            fontWeight: 600,
            display: 'inline-flex',
            alignItems: 'center',
            flexShrink: 0,
            fontFamily:
              "'Google Sans Text', 'Google Sans', Roboto, Arial, sans-serif",
          }}
        >
          {confirmLabel}
        </button>
      </div>
    </BlockList>
  );
}

function Section({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      <div
        style={{
          fontSize: 13,
          fontWeight: 500,
          color: 'var(--text-secondary)',
          padding: '0 4px',
          fontFamily:
            "'Google Sans Text', 'Google Sans', Roboto, Arial, sans-serif",
        }}
      >
        {title}
      </div>
      {children}
    </div>
  );
}

function BlockList({ children }: { children: React.ReactNode }) {
  return (
    <div
      style={{
        background: 'rgba(0,0,0,0.04)',
        borderRadius: 20,
        overflow: 'hidden',
      }}
    >
      {children}
    </div>
  );
}

function BlockRow({
  iconName,
  title,
  desc,
  onPress,
  isLast,
}: {
  iconName: string;
  title: string;
  desc: string;
  onPress: () => void;
  isLast: boolean;
}) {
  const [hover, setHover] = useState(false);
  return (
    <button
      type="button"
      onClick={onPress}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        all: 'unset',
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        padding: '12px 16px',
        cursor: 'pointer',
        background: hover ? 'rgba(0,0,0,0.04)' : 'transparent',
        borderBottom: isLast ? 'none' : '1px solid var(--gem-outline-variant)',
        transition: 'background-color 140ms',
        boxSizing: 'border-box',
      }}
    >
      <div
        style={{
          flexShrink: 0,
          width: 36,
          height: 36,
          borderRadius: 18,
          background: 'rgba(0,0,0,0.06)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: 'var(--text-primary)',
        }}
      >
        <MaterialSymbol name={iconName} size={18} />
      </div>
      <div
        style={{
          flex: 1,
          minWidth: 0,
          display: 'flex',
          flexDirection: 'column',
          gap: 2,
        }}
      >
        <div
          style={{
            fontSize: 15,
            fontWeight: 500,
            color: 'var(--text-primary)',
            lineHeight: '20px',
            fontFamily:
              "'Google Sans Text', 'Google Sans', Roboto, Arial, sans-serif",
          }}
        >
          {title}
        </div>
        <div
          style={{
            fontSize: 13,
            lineHeight: '18px',
            color: 'var(--text-tertiary)',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            fontFamily:
              "'Google Sans Text', 'Google Sans', Roboto, Arial, sans-serif",
          }}
        >
          {desc}
        </div>
      </div>
      <MaterialSymbol
        name="chevron_right"
        size={20}
        style={{ color: 'var(--text-tertiary)', flexShrink: 0 }}
      />
    </button>
  );
}

function QuickAddTile({
  iconName,
  label,
  onPress,
}: {
  iconName: string;
  label: string;
  onPress: () => void;
}) {
  const [hover, setHover] = useState(false);
  return (
    <button
      type="button"
      onClick={onPress}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        all: 'unset',
        cursor: 'pointer',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
        gap: 12,
        padding: '16px',
        minHeight: 96,
        borderRadius: 20,
        background: hover ? 'rgba(0,0,0,0.06)' : 'rgba(0,0,0,0.04)',
        transition: 'background-color 140ms',
        boxSizing: 'border-box',
      }}
    >
      <div
        style={{
          width: 36,
          height: 36,
          borderRadius: 18,
          background: 'rgba(0,0,0,0.06)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: 'var(--text-primary)',
        }}
      >
        <MaterialSymbol name={iconName} size={18} />
      </div>
      <div
        style={{
          fontSize: 14,
          fontWeight: 500,
          color: 'var(--text-primary)',
          lineHeight: '18px',
          fontFamily:
            "'Google Sans Text', 'Google Sans', Roboto, Arial, sans-serif",
        }}
      >
        {label}
      </div>
    </button>
  );
}

function ExploreTile({
  iconName,
  label,
  count,
  onPress,
}: {
  iconName: string;
  label: string;
  count: number;
  onPress: () => void;
}) {
  const [hover, setHover] = useState(false);
  return (
    <button
      type="button"
      onClick={onPress}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        all: 'unset',
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        padding: '12px 14px',
        borderRadius: 16,
        background: hover ? 'rgba(0,0,0,0.06)' : 'rgba(0,0,0,0.04)',
        transition: 'background-color 140ms',
        boxSizing: 'border-box',
      }}
    >
      <div
        style={{
          flexShrink: 0,
          width: 32,
          height: 32,
          borderRadius: 16,
          background: 'rgba(0,0,0,0.06)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: 'var(--text-primary)',
        }}
      >
        <MaterialSymbol name={iconName} size={16} />
      </div>
      <div
        style={{
          flex: 1,
          minWidth: 0,
          display: 'flex',
          flexDirection: 'column',
          gap: 2,
        }}
      >
        <div
          style={{
            fontSize: 14,
            fontWeight: 500,
            color: 'var(--text-primary)',
            lineHeight: '18px',
            fontFamily:
              "'Google Sans Text', 'Google Sans', Roboto, Arial, sans-serif",
          }}
        >
          {label}
        </div>
        <div
          style={{
            fontSize: 12,
            color: 'var(--text-tertiary)',
            lineHeight: '16px',
            fontFamily:
              "'Google Sans Text', 'Google Sans', Roboto, Arial, sans-serif",
          }}
        >
          {count} blocks
        </div>
      </div>
      <MaterialSymbol
        name="chevron_right"
        size={18}
        style={{ color: 'var(--text-tertiary)', flexShrink: 0 }}
      />
    </button>
  );
}

function SearchPill({
  value,
  onChange,
  placeholder,
  iconName = 'search',
}: {
  value: string;
  onChange: (next: string) => void;
  placeholder: string;
  iconName?: string;
}) {
  const [focused, setFocused] = useState(false);

  return (
    <div
      style={{
        height: 48,
        borderRadius: 9999,
        background: 'var(--input-bg)',
        border: focused
          ? '1px solid var(--text-secondary)'
          : '1px solid var(--input-border)',
        boxShadow: focused ? 'none' : 'var(--gem-input-shadow)',
        display: 'flex',
        alignItems: 'center',
        padding: '0 14px',
        gap: 10,
        transition: 'border-color 140ms, box-shadow 140ms',
      }}
    >
      <MaterialSymbol
        name={iconName}
        size={18}
        style={{ color: 'var(--text-tertiary)' }}
      />
      <input
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onFocus={() => setFocused(true)}
        onBlur={() => setFocused(false)}
        placeholder={placeholder}
        style={{
          flex: 1,
          height: '100%',
          padding: 0,
          border: 0,
          outline: 0,
          background: 'transparent',
          fontSize: 14,
          color: 'var(--text-primary)',
          fontFamily:
            "'Google Sans Text', 'Google Sans', Roboto, Arial, sans-serif",
        }}
      />
      {value ? (
        <button
          type="button"
          onClick={() => onChange('')}
          aria-label="Clear"
          style={{
            all: 'unset',
            cursor: 'pointer',
            width: 24,
            height: 24,
            borderRadius: 12,
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: 'var(--text-tertiary)',
          }}
        >
          <MaterialSymbol name="close" size={16} />
        </button>
      ) : null}
    </div>
  );
}

function EmptyState({
  label,
  fallback,
}: {
  label: string;
  fallback: string;
}) {
  const text = label && !label.startsWith('publicProfile.') ? label : fallback;
  return (
    <div
      style={{
        padding: '24px 12px',
        textAlign: 'center',
        fontSize: 13,
        color: 'var(--text-tertiary)',
        fontFamily:
          "'Google Sans Text', 'Google Sans', Roboto, Arial, sans-serif",
      }}
    >
      {text}
    </div>
  );
}
